import { fetchApi } from '@inmagik/react-crud'
import { UseQueryResult } from '@tanstack/react-query'
import dayjs from 'dayjs'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useMemo, useRef, useState } from 'react'
import { Button, Card, CardBody, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useAuth } from '../../auth'
import MaterialIcon from '../../components/MaterialIcon'
import ModalConfirm from '../../components/ModalConfirm'
import { API_URL } from '../../constants'
import { usePartialUpdateBilancia } from '../../hooks/bilance'
import { useCreateAllegatoIntervento, usePartialUpdateIntervento } from '../../hooks/interventi'
import { AllegatoIntervento, Intervento, PaginatedDJResponse } from '../../types'
import { transformDownloadUrl, useIsAmministratore, useIsCliente, useIsSupervisore } from '../../utils/utils'

interface FlussoRapportoTaraturaInterface {
  id: Intervento['id']
  data_intervento: Intervento['data_intervento']
  bilancia_data?: Intervento['bilancia_data']
  bilancia: Intervento['bilancia']
  foto_targa?: Intervento['foto_targa']
  stato: Intervento['stato']
  esito_xlsx?: Intervento['esito_xlsx']
  esito: Intervento['esito']
  attestato_taratura?: Intervento['attestato_taratura']
  data_invio?: Intervento['data_invio']
}

type CardFlussoRapportoTaraturaProps = {
  intervento: UseQueryResult<FlussoRapportoTaraturaInterface, Error>
  allegatiIntervento: UseQueryResult<PaginatedDJResponse<AllegatoIntervento>, Error>
}

export default function CardFlussoRapportoTaratura({
  intervento,
  allegatiIntervento,
}: CardFlussoRapportoTaraturaProps) {
  const isCliente = useIsCliente()
  const fotoTargaInputRef = useRef<HTMLInputElement>(null)
  const esitoInterventoInputRef = useRef<HTMLInputElement>(null)
  const allegatiInputRef = useRef<HTMLInputElement>(null)

  const { mutateAsync: updatePartialIntervento } = usePartialUpdateIntervento()
  const { mutateAsync: createAllegatoIntervento } = useCreateAllegatoIntervento()
  const { tokens, user } = useAuth()

  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false)
  const [isDownloadingCert, setIsDownloadingCert] = useState(false)

  const isBilanciaWithoutTipoBilanciaEm = useMemo(() => {
    if (!intervento.data?.bilancia_data?.tipo_bilancia_em) {
      return true
    } else {
      return false
    }
  }, [intervento.data?.bilancia_data?.tipo_bilancia_em])

  const { mutateAsync: updatePartialBilanica } = usePartialUpdateBilancia()
  const [tipoBilanciaEm, setTipoBilanciaEm] = useState('')
  const [isRigeneraCert, setIsRigeneraCert] = useState(false)
  const [isSendingCertificato, setIsSendingCertificato] = useState(false)
  const [modalStateInviaMail, modalActionsInviaMail] = useModalTrigger()
  const isSupervisore = useIsSupervisore()
  const isAmministratore = useIsAmministratore()

  return (
    <Card>
      <CardBody>
        <h5 className="heading-h5">Rapporto di taratura</h5>
        {!isCliente && isBilanciaWithoutTipoBilanciaEm && (
          <div className="border border-danger p-3 mt-3">
            <div className="text-danger">
              La bilancia non ha un tipo di bilancia (Elettronica/Meccanica) impostato. Aggiorna la bilancia per poter
              procedere con la taratura.
            </div>

            <select
              className="form-select my-3"
              value={tipoBilanciaEm}
              onChange={(e) => setTipoBilanciaEm(e.target.value)}
            >
              <option value="">Seleziona un tipo di bilancia</option>
              <option value="pesa_elettronica">Pesa elettronica</option>
              <option value="pesa_meccanica">Pesa meccanica</option>
            </select>
            <Button
              variant="secondary"
              disabled={!tipoBilanciaEm}
              className="text-white d-flex align-items-center"
              onClick={async () => {
                await updatePartialBilanica({
                  id: intervento.data?.bilancia as number,
                  tipo_bilancia_em: tipoBilanciaEm,
                }).then(() => {
                  toast.success('Tipo bilancia impostato con successo', {
                    position: 'bottom-right',
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                  })
                  intervento.refetch()
                })
              }}
            >
              Salva
            </Button>
          </div>
        )}
        {!isCliente && (
          <div className="d-flex align-items-center justify-content-between my-4">
            <span className="p-md">Scarica l’excel pre-compilato per effettuare la taratura.</span>
            <Button
              variant="secondary"
              disabled={
                intervento?.data?.stato === 'eseguito' ||
                intervento.data?.stato === 'annullato' ||
                isBilanciaWithoutTipoBilanciaEm
              }
              onClick={async () => {
                try {
                  setIsDownloadingExcel(true)
                  const data = await fetchApi(
                    `${API_URL}/bilance/intervento/${intervento?.data?.id}/download-excel-rapporto-taratura/`,
                    {
                      method: 'GET',

                      headers: {
                        Authorization: 'Bearer ' + tokens?.access,
                      },
                    }
                  )
                  const blob = await data.blob()
                  const contentDisposition = data.headers.get('Content-Disposition')
                  const idIntervento = intervento?.data?.id
                  const dataIntervento = dayjs(intervento?.data?.data_intervento).format('DD-MM-YYYY')
                  const filename = contentDisposition
                    ? contentDisposition.split('filename=')[1].replace(/"/g, '')
                    : `rapporto-taratura-${idIntervento}-${dataIntervento}.xlsx`
                  const url = window.URL.createObjectURL(new Blob([blob]))
                  const link = document.createElement('a')
                  link.href = url
                  link.setAttribute('download', filename)
                  document.body.appendChild(link)
                  link.click()
                  link.parentNode?.removeChild(link)
                } catch (e) {
                  toast.error("Errore nel download dell'excel", {
                    position: 'bottom-right',
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                  })
                } finally {
                  setIsDownloadingExcel(false)
                }
              }}
              className="text-white d-flex align-items-center"
            >
              <span
                className="material-icons me-2"
                style={{
                  fontSize: '16px',
                }}
              >
                download
              </span>
              Scarica excel
              {isDownloadingExcel && <Spinner animation="border" role="status" size="sm" className="ms-4" />}
            </Button>
          </div>
        )}
        <div>
          <div className="heading-h6">Allegati</div>
          <div className="mt-4">
            <div className="d-flex label-m-bold d-flex align-items-center">
              <span className="material-icons me-2">photo_camera</span>
              <span>Foto targa</span>
            </div>
            {!isCliente && <div className="mt-2">Inserisci una foto della targa della macchina sotto taratura.</div>}
            <div className="mt-3 d-flex align-items-center">
              {!isCliente && !intervento?.data?.foto_targa && (
                <>
                  <Button
                    variant="outline-secondary"
                    disabled={
                      intervento?.data?.stato === 'annullato' ||
                      intervento?.data?.stato === 'non_eseguito' ||
                      isBilanciaWithoutTipoBilanciaEm
                    }
                    className="text-secondary d-flex align-items-center"
                    onClick={() => {
                      // avoid problem with same file selection

                      fotoTargaInputRef.current?.click()
                    }}
                  >
                    <span className="material-icons">attach_file</span>
                  </Button>
                  <span className="ms-2">Inserisci allegato</span>
                </>
              )}
              {intervento?.data?.foto_targa && (
                <div className="w-100">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <a href={transformDownloadUrl(intervento?.data?.foto_targa as string)} target="_blank">
                        <img
                          src={
                            typeof intervento?.data?.foto_targa === 'string'
                              ? transformDownloadUrl(intervento?.data?.foto_targa)
                              : URL.createObjectURL(intervento?.data?.foto_targa)
                          }
                          alt="Foto targa"
                          className="me-2"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                          }}
                        />
                      </a>
                      <span className="p-md">
                        {/* name of the file from the path */}
                        {typeof intervento?.data?.foto_targa === 'string'
                          ? intervento?.data?.foto_targa.split('/').pop()?.split('?')[0]
                          : intervento?.data?.foto_targa.name.split('?')[0]}
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      {!isCliente && intervento.data.stato === 'programmato' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="text-white d-flex align-items-center me-2"
                          onClick={() => {
                            fotoTargaInputRef.current?.click()
                          }}
                        >
                          <span
                            className="material-icons"
                            style={{
                              fontSize: 20,
                            }}
                          >
                            upload
                          </span>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="d-flex align-items-center"
                        onClick={() => {
                          typeof intervento?.data?.foto_targa === 'string' && window.open(intervento?.data?.foto_targa)
                        }}
                      >
                        <span
                          className="material-icons me-2"
                          style={{
                            fontSize: 16,
                          }}
                        >
                          download
                        </span>
                        Scarica
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {!isCliente && (
                <input
                  ref={fotoTargaInputRef}
                  type="file"
                  className="d-none"
                  accept="image/*"
                  onChange={async (e) => {
                    if (e.target.files && e.target.files[0]) {
                      await updatePartialIntervento({
                        id: intervento?.data?.id,
                        foto_targa: e.target.files[0],
                      })
                        .then(() => {
                          toast.success('Foto targa aggiunta con successo', {
                            position: 'bottom-right',
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                          })
                        })
                        .finally(() => {
                          e.target.value = ''
                        })
                    }
                  }}
                />
              )}
            </div>
            {!isCliente && (
              <div className="mt-4">
                <div className="d-flex label-m-bold d-flex align-items-center">
                  <span className="material-icons me-2">description</span>
                  <span>Esito intervento</span>
                </div>
                <div className="mt-2">Dopo aver scaricato l’excel , ricaricalo qui compilato e firmato.</div>
                <div className="mt-3 d-flex align-items-center">
                  {!intervento?.data?.esito_xlsx ? (
                    <>
                      <Button
                        variant="outline-secondary"
                        className="text-secondary d-flex align-items-center"
                        disabled={
                          intervento?.data?.stato === 'annullato' ||
                          intervento?.data?.stato === 'non_eseguito' ||
                          isBilanciaWithoutTipoBilanciaEm
                        }
                        onClick={() => {
                          esitoInterventoInputRef.current?.click()
                        }}
                      >
                        <span className="material-icons">attach_file</span>
                      </Button>
                      <span className="ms-2">Inserisci allegato</span>
                    </>
                  ) : (
                    <div className="w-100">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <span className="material-icons text-secondary me-2">description</span>
                          <span className="p-md">
                            {/* name of the file from the path */}
                            {typeof intervento?.data?.esito_xlsx === 'string'
                              ? intervento?.data?.esito_xlsx.split('/').pop()?.split('?')[0]
                              : intervento?.data?.esito_xlsx.name.split('?')[0]}
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          {intervento.data.stato === 'programmato' && (
                            <Button
                              size="sm"
                              variant="secondary"
                              className="text-white d-flex align-items-center me-2"
                              onClick={() => {
                                esitoInterventoInputRef.current?.click()
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{
                                  fontSize: 20,
                                }}
                              >
                                upload
                              </span>
                            </Button>
                          )}
                          <Button
                            size="sm"
                            as="a"
                            href={
                              typeof intervento?.data?.esito_xlsx === 'string'
                                ? transformDownloadUrl(intervento?.data?.esito_xlsx)
                                : '#'
                            }
                            variant="outline-secondary"
                            target="_blank"
                            className="d-flex align-items-center"
                          >
                            <span
                              className="material-icons me-2"
                              style={{
                                fontSize: 16,
                              }}
                            >
                              download
                            </span>
                            Scarica
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  <input
                    ref={esitoInterventoInputRef}
                    type="file"
                    className="d-none"
                    accept=".xltm, .xlsm, .xlsx"
                    onChange={async (e) => {
                      if (e.target.files && e.target.files[0]) {
                        await updatePartialIntervento({
                          id: intervento?.data?.id,
                          esito_xlsx: e.target.files[0],
                          utente: user?.id,
                        })
                          .then(async () => {
                            toast.success('Esito intervento aggiunto con successo', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                            await fetchApi(`${API_URL}/bilance/intervento/${intervento?.data?.id}/esito-intervento/`, {
                              method: 'GET',

                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: 'Bearer ' + tokens?.access,
                              },
                            }).then((res) => {
                              intervento.refetch()
                            })
                          })
                          .finally(() => {
                            e.target.value = ''
                          })
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4">
          <div className="d-flex label-m-bold d-flex align-items-center">
            <span>Altri allegati</span>
          </div>
          {!isCliente && <div className="mt-2">Inserisci qui qualsiasi altro documento della verificazione.</div>}
        </div>
        {allegatiIntervento?.data?.results && allegatiIntervento?.data?.results.length === 0 && (
          <div className="mt-3 text-secondary">Nessun allegato presente</div>
        )}
        {allegatiIntervento?.data?.results && allegatiIntervento?.data?.results.length > 0 && (
          <div className="mt-3">
            {allegatiIntervento?.data?.results.map((allegato) => (
              <div key={allegato.id} className="d-flex align-items-center justify-content-between mt-3">
                <div className="d-flex align-items-center">
                  <span className="material-icons me-2 bg-secondary-light p-2 rounded text-secondary">description</span>
                  <span>{String(allegato.allegato).split('/').pop()?.split('?')[0]}</span>
                </div>
                <div className="d-flex align-items-center">
                  {!isCliente && intervento?.data?.stato === 'programmato' && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      className="d-flex align-items-center me-2"
                      onClick={async () => {
                        await fetchApi(`${API_URL}/bilance/allegato-intervento/${allegato.id}/`, {
                          method: 'DELETE',
                          headers: {
                            Authorization: 'Bearer ' + tokens?.access,
                          },
                        }).then(() => {
                          toast.success('Allegato rimosso con successo', {
                            position: 'bottom-right',
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                          })
                          intervento.refetch()
                          allegatiIntervento.refetch()
                        })
                      }}
                    >
                      <span className="material-icons">delete</span>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="text-secondary d-flex align-items-center"
                    onClick={() => {
                      window.open(String(allegato.allegato))
                    }}
                  >
                    <span className="material-icons me-2">download</span>
                    Scarica
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 d-flex align-items-center">
          {!isCliente && intervento.data?.stato === 'programmato' && (
            <>
              <Button
                variant="outline-secondary"
                className="text-secondary d-flex align-items-center"
                // disabled={intervento?.data?.stato === 'annullato'}
                onClick={() => {
                  allegatiInputRef.current?.click()
                }}
                disabled={isBilanciaWithoutTipoBilanciaEm}
              >
                <span className="material-icons">add</span>
              </Button>
              <span className="ms-2">Aggiungi allegato</span>
            </>
          )}

          {!isCliente && (
            <input
              ref={allegatiInputRef}
              type="file"
              className="d-none"
              onChange={async (e) => {
                if (e.target.files && e.target.files[0] && intervento.data) {
                  await createAllegatoIntervento({
                    intervento: intervento.data.id,
                    allegato: e.target.files[0],
                  })
                    .then(() => {
                      toast.success('Allegato aggiunto con successo', {
                        position: 'bottom-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                      })
                      intervento.refetch()
                    })
                    .finally(() => {
                      e.target.value = ''
                    })
                }
              }}
            />
          )}
        </div>
        {intervento?.data?.esito_xlsx && (
          <div className="d-flex align-items-center mt-5 justify-content-between">
            <div className="d-flex align-items-center">
              <div className="heading-h4">Esito certificazione</div>
              <div>
                {intervento.data?.esito !== '' && (
                  <div className="d-flex align-items-center ms-4">
                    {intervento.data?.esito === 'positivo' ? (
                      <div className="d-flex align-items-center">
                        <span className="material-icons me-2 text-success">check</span>
                        <span className="heading-h3 text-capitalize">{intervento.data.esito}</span>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center">
                        <span className="material-icons me-2 text-danger">close</span>
                        <span className="heading-h3 text-capitalize">{intervento.data?.esito}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              {!isCliente && intervento.data.stato === 'programmato' && (
                <Button
                  variant="secondary"
                  className="text-white d-flex align-items-center"
                  onClick={async () => {
                    await updatePartialIntervento({
                      id: intervento?.data?.id,
                      stato: 'eseguito',
                    }).then(() => {
                      toast.success('Esito intervento impostato a eseguito', {
                        position: 'bottom-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                      })
                      intervento.refetch()
                    })
                  }}
                >
                  Chiudi intervento
                </Button>
              )}
              {!isCliente && intervento.data.stato === 'eseguito' && (
                <div className="text-success">Intervento chiuso con successo!</div>
              )}
            </div>
          </div>
        )}
        <div className="d-flex align-items-center justify-content-end mt-4">
          {intervento.data?.stato === 'eseguito' && intervento.data?.esito_xlsx && (
            <div className="mt-2">
              <div className="d-flex align-items-center justify-content-end">
                <Button
                  variant="secondary"
                  className="text-white d-flex align-items-center"
                  onClick={async () => {
                    if (typeof intervento?.data?.attestato_taratura === 'string') {
                      window.open(intervento?.data?.attestato_taratura)
                    } else {
                      try {
                        setIsDownloadingCert(true)
                        const data = await fetchApi(
                          `${API_URL}/bilance/intervento/${intervento?.data?.id}/download-certificato-taratura/`,
                          {
                            method: 'GET',

                            headers: {
                              Authorization: 'Bearer ' + tokens?.access,
                            },
                          }
                        )
                        const blob = await data.blob()
                        const url = window.URL.createObjectURL(new Blob([blob]))
                        const link = document.createElement('a')
                        link.href = url
                        link.setAttribute(
                          'download',
                          `rapporto-taratura-${intervento?.data?.id}-${dayjs(intervento?.data?.data_intervento).format(
                            'DD-MM-YYYY'
                          )}.pdf`
                        )
                        document.body.appendChild(link)
                        link.click()
                        link.parentNode?.removeChild(link)
                      } catch (e) {
                        toast.error('Errore nel download del certificato', {
                          position: 'bottom-right',
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                        })
                      } finally {
                        setIsDownloadingCert(false)
                      }
                    }
                  }}
                  disabled={isDownloadingCert}
                >
                  <MaterialIcon icon="download" className="me-2" />
                  <span>Scarica certificato</span>
                  {isDownloadingCert && <Spinner animation="border" role="status" size="sm" className="ms-4" />}
                </Button>
              </div>
            </div>
          )}
          {!isCliente && intervento.data?.stato === 'eseguito' && intervento.data?.esito_xlsx && (
            <div className="ms-2 mt-2">
              <div className="d-flex align-items-center justify-content-end">
                <Button
                  variant="outline-secondary"
                  className="text-secondary d-flex align-items-center"
                  onClick={async () => {
                    try {
                      setIsRigeneraCert(true)
                      const data = await fetchApi(
                        `${API_URL}/bilance/intervento/${intervento?.data?.id}/rigenera-certificato-taratura/`,
                        {
                          method: 'GET',

                          headers: {
                            Authorization: 'Bearer ' + tokens?.access,
                          },
                        }
                      )
                      const blob = await data.blob()
                      const url = window.URL.createObjectURL(new Blob([blob]))
                      const link = document.createElement('a')
                      link.href = url
                      link.setAttribute(
                        'download',
                        `rapporto-taratura-${intervento?.data?.id}-${dayjs(intervento?.data?.data_intervento).format(
                          'DD-MM-YYYY'
                        )}.pdf`
                      )
                      document.body.appendChild(link)
                      link.click()
                      link.parentNode?.removeChild(link)
                      setIsRigeneraCert(false)
                    } catch (e) {
                      toast.error('Errore nella rigenerazione del certificato', {
                        position: 'bottom-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                      })
                    } finally {
                      setIsRigeneraCert(false)
                    }
                  }}
                  disabled={isDownloadingCert}
                >
                  <MaterialIcon icon="refresh" className="me-2" />
                  <span>Rigenera certificato</span>
                  {isRigeneraCert && <Spinner animation="border" role="status" size="sm" className="ms-4" />}
                </Button>
              </div>
            </div>
          )}
        </div>
        {!isCliente && intervento?.data?.stato === 'eseguito' && (
          <div>
            <hr />
            {intervento.data.data_invio && (
              <div>
                Data ultimo invio: <strong>{dayjs(intervento.data.data_invio).format('DD/MM/YYYY')}</strong>
              </div>
            )}
            <div className="mt-2 d-flex align-items-center justify-content-start">
              <Button
                variant="outline-secondary"
                disabled={isSendingCertificato}
                size="sm"
                className="text-secondary d-flex align-items-center"
                onClick={async () => {
                  modalActionsInviaMail.open(intervento.data)
                }}
              >
                <span className="material-icons me-2">email</span>
                <span>{intervento.data.data_invio ? 'Rinvia' : 'Invia'} certificato</span>
                {isSendingCertificato && <Spinner animation="border" variant="secondary" className="ms-4" size="sm" />}
              </Button>
            </div>
          </div>
        )}
      </CardBody>
      <ModalConfirm
        title="Invia certificato rapporto taratura"
        isOpen={modalStateInviaMail.isOpen}
        toggle={modalActionsInviaMail.toggle}
        confirmText="Invia"
        cancelText="Annulla"
        body={`Vuoi ${
          intervento?.data?.data_invio ? 'rinviare' : 'inviare'
        } il certificato al cliente al seguente indirizzo: ${
          intervento?.data?.bilancia_data?.sede_data?.email_referente
        }`}
        action={async () => {
          setIsSendingCertificato(true)
          await fetchApi(`${API_URL}/bilance/intervento/${intervento?.data?.id}/invia-certificato-taratura/`, {
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + tokens?.access,
            },
          }).then(() => {
            setIsSendingCertificato(false)
            intervento.refetch()
            modalActionsInviaMail.close()
            toast.success('Certificato inviato con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
          })
        }}
      />
    </Card>
  )
}
